<template>
    <div class="pfeb-block">
        <div class="pfeb-hdr" :class="{ open }" :data-num="index + 1" @click="$emit('toggle')">
            <span class="pfeb-drag" @click.stop><v-icon size="16">mdi-drag-vertical</v-icon></span>
            <div class="pfeb-block-info">
                <div class="pfeb-name">{{ blockName }}</div>
                <div class="pfeb-meta">
                    <span :class="{ 'pfeb-meta-error': exercises.length === 0 }">
                        <v-icon size="12">mdi-dumbbell</v-icon>{{ exercises.length }}
                        {{ $tc('common.exercises', exercises.length).toLowerCase() }}
                    </span>
                </div>
            </div>
            <v-menu offset-y>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn icon small class="pfeb-ctx-btn" v-on="on" v-bind="attrs" @click.stop>
                        <v-icon size="18">mdi-dots-horizontal</v-icon>
                    </v-btn>
                </template>
                <v-list dense>
                    <v-list-item link @click="$emit('change-name')">
                        <v-icon size="18" color="txtSecondary" class="mr-2">mdi-pencil-outline</v-icon>
                        <v-list-item-title>{{ $t('common.change_name') }}</v-list-item-title>
                    </v-list-item>
                    <v-list-item link @click="$emit('duplicate')">
                        <v-icon size="18" color="txtSecondary" class="mr-2">mdi-content-copy</v-icon>
                        <v-list-item-title>{{ $t('common.duplicate') }}</v-list-item-title>
                    </v-list-item>
                    <v-divider />
                    <v-list-item link @click="$emit('delete')">
                        <v-icon size="18" color="error" class="mr-2">mdi-trash-can-outline</v-icon>
                        <v-list-item-title class="error--text">{{ $t('common.delete') }}</v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-menu>
            <v-icon size="18" class="pfeb-chevron" :class="{ open }">mdi-chevron-down</v-icon>
        </div>

        <div v-if="open" class="pfeb-body">
            <draggable v-model="innerExercises" :group="{ name: 'routine-exercises' }" draggable=".es-wrap"
                handle=".es-drag" ghost-class="ghost" class="rb-exlist">
                <div v-for="(exercise, j) in exercises" :key="exercise.uid" class="es-wrap"
                    :class="{ open: expandedIndex === j }">
                    <div class="es-ej-item" @click="toggleExpand(j)">
                        <span class="es-drag" @click.stop><v-icon size="16">mdi-drag-vertical</v-icon></span>
                        <div class="es-ej-thumb" :class="{ 'has-video': hasVideo(exercise) }"
                            :style="thumbStyle(exercise)" @click.stop="onShowVideo(exercise)"></div>
                        <div class="es-ej-info">
                            <div class="es-ej-name">{{ exercise.name }}</div>
                            <div class="es-ej-sets" :class="{ 'es-ej-sets-error': isUnconfigured(exercise) }">
                                {{ setsSummary(exercise) }}
                            </div>
                        </div>
                        <v-menu offset-y>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn icon small class="es-ej-ctx" v-on="on" v-bind="attrs" @click.stop>
                                    <v-icon size="18">mdi-dots-horizontal</v-icon>
                                </v-btn>
                            </template>
                            <v-list dense>
                                <v-list-item link @click="changeExerciseAt(j)">
                                    <v-icon size="18" color="txtSecondary" class="mr-2">mdi-swap-horizontal</v-icon>
                                    <v-list-item-title>{{ $t('common.change') }}</v-list-item-title>
                                </v-list-item>
                                <v-list-item link @click="duplicateExerciseAt(j)">
                                    <v-icon size="18" color="txtSecondary" class="mr-2">mdi-content-copy</v-icon>
                                    <v-list-item-title>{{ $t('common.duplicate') }}</v-list-item-title>
                                </v-list-item>
                                <v-divider />
                                <v-list-item link @click="confirmDeleteExercise(j)">
                                    <v-icon size="18" color="error" class="mr-2">mdi-trash-can-outline</v-icon>
                                    <v-list-item-title class="error--text">{{ $t('common.delete') }}</v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                        <v-icon size="18" class="es-ej-chev" :class="{ open: expandedIndex === j }">mdi-chevron-down</v-icon>
                    </div>
                    <div v-if="expandedIndex === j" class="es-ej-detail">
                        <exercise-series-editor :exercise="exercise" :category-exercise="exerciseCategory(exercise)"
                            :circuit-type="block.isCircuit ? block.type : null" @update="onExerciseUpdate(j, $event)"
                            @click:rest="setRest(j)" />
                    </div>
                </div>
                <div v-if="exercises.length === 0" class="rb-empty">{{ $t('exercises.no_exercises_set') }}</div>
            </draggable>

            <v-btn v-if="!block.isCircuit" text block :color="accentColor" class="pfeb-add-ej"
                @click="showAddExercisesDialog">
                <v-icon left size="16">mdi-plus</v-icon>{{ $t('common.add_exercise') }}
            </v-btn>

            <rest-dialog ref="setRestDialog" is-second-dialog @save="onRestSave" />
            <add-exercises-dialog ref="addExerciseDialog" is-second-dialog :title="blockName"
                :one-exercise="oneExercise" :default-filter="defaultFilter" @change="addExercises" />
            <watch-video-dialog ref="watchVideo" local :thumb="currentImg" :video="currentVideo" />
            <kimsa-confirm ref="confirmDeleteEx" is-second-dialog alert-mode :title="$t('common.delete')"
                :content="$t('common.confirm_delete')" @confirm="doDeleteExercise" @cancel="pendingDeleteEx = null" />
        </div>
    </div>
</template>

<script>
import { randomString } from "@/utils/Common";
import ExerciseSeriesEditor from "@/components/coach/routines/ExerciseSeriesEditor";
import RestDialog from "@/components/coach/routines/RestDialog";
import AddExercisesDialog from "@/components/coach/routines/AddExercisesDialog";
import WatchVideoDialog from "@/components/common/routines/WatchVideoDialog";
import KimsaConfirm from "@/components/common/KimsaConfirm";
import draggable from "vuedraggable";

export default {
    name: "RoutineBlockItem",
    components: { ExerciseSeriesEditor, RestDialog, AddExercisesDialog, WatchVideoDialog, KimsaConfirm, draggable },
    props: {
        block: {
            type: Object,
            required: true,
        },
        index: {
            type: Number,
            default: 0,
        },
        open: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            expandedIndex: null,
            pendingDeleteEx: null,
            oneExercise: false,
            exerciseIndex: null,
            activeExerciseIndex: null,
            defaultFilter: null,
            currentImg: null,
            currentVideo: null,
        }
    },
    computed: {
        accentColor(vm = this) {
            return vm.$vuetify.theme.dark ? 'primary' : 'accent'
        },
        exercises(vm = this) {
            return vm.block.exercises || []
        },
        innerExercises: {
            get(vm = this) {
                return vm.block.exercises || []
            },
            set(val) {
                this.$emit('update', { ...this.block, exercises: val })
            },
        },
        blockName(vm = this) {
            return vm.blockNameLabel(vm.block.name) || `${vm.$tc('common.blocks', 1)} ${vm.index + 1}`
        },
    },
    methods: {
        emitExercises(exercises) {
            this.$emit('update', { ...this.block, exercises })
        },
        toggleExpand(j) {
            this.expandedIndex = this.expandedIndex === j ? null : j
        },
        exerciseCategory(exercise) {
            return exercise?.categories?.[0] || exercise?.category || null
        },
        isUnconfigured(exercise) {
            const vm = this
            if (vm.block.isCircuit) {
                if (vm.block.type === 'tabata') return false
                return !exercise.config || !exercise.config.type || exercise.config.value === null || exercise.config.value === undefined || exercise.config.value === ''
            }
            return !(exercise.config && exercise.config.sets && exercise.config.sets.length > 0)
        },
        setsSummary(exercise) {
            const vm = this
            if (vm.block.isCircuit) {
                if (vm.block.type === 'tabata') return vm.$t('circuits.tabata')
                const value = exercise?.config?.value
                const type = vm.$t(`exercises.short_${exercise?.config?.type || 'reps'}`)
                return `${vm.block.sets || 1} × ${value != null ? value : ''} ${type}`.trim()
            }
            const sets = exercise?.config?.sets || []
            if (!sets.length) return vm.$t('exercises.no_config_exercise_yet')
            const first = sets[0]?.data?.[0]
            const typeKey = (first?.short_name || 'short_reps').replace('short_', '')
            const typeLabel = vm.$t(`exercises.${typeKey}`).toLowerCase()
            let summary = `${sets.length} × ${first && first.value != null ? first.value : ''} ${typeLabel}`
            if (exercise.eachSide) summary += ` · ${vm.$t('exercises.each_side')}`
            return summary
        },
        imageOf(exercise) {
            if (exercise?.youtubeUrl) return this.getYoutubeImgUrl(exercise.youtubeUrl)
            return exercise?.img || exercise?.video?.thumb || this.imgPlaceholder
        },
        thumbStyle(exercise) {
            return { backgroundImage: `url('${this.imageOf(exercise)}')` }
        },
        hasVideo(exercise) {
            return !!(exercise?.video?.url || exercise?.youtubeUrl)
        },
        onShowVideo(exercise) {
            const vm = this
            if (!vm.hasVideo(exercise)) return
            vm.currentImg = exercise.img || exercise.video?.thumb || null
            vm.currentVideo = exercise.video?.url || exercise.youtubeUrl || null
            vm.$refs.watchVideo.show(!!exercise.youtubeUrl)
        },
        showAddExercisesDialog() {
            const vm = this
            vm.oneExercise = false
            vm.$refs.addExerciseDialog.show()
        },
        addExercises(exercises) {
            const vm = this
            if (vm.oneExercise) {
                const list = [...vm.exercises]
                list.splice(vm.exerciseIndex, 1, { ...exercises[0], type: 'exercise', uid: randomString() })
                return vm.emitExercises(list)
            }
            const newExercises = exercises.map(ex => ({ ...ex, type: 'exercise', uid: randomString() }))
            vm.emitExercises([...vm.exercises, ...newExercises])
        },
        duplicateExerciseAt(j) {
            const vm = this
            const copy = { ...JSON.parse(JSON.stringify(vm.exercises[j])), uid: randomString() }
            const list = [...vm.exercises]
            list.splice(j + 1, 0, copy)
            vm.emitExercises(list)
        },
        changeExerciseAt(j) {
            const vm = this
            const exercise = vm.exercises[j]
            vm.exerciseIndex = j
            vm.oneExercise = true
            vm.defaultFilter = { categories: exercise.categories?.[0], types: exercise.types }
            vm.$refs.addExerciseDialog.show()
        },
        confirmDeleteExercise(j) {
            const vm = this
            vm.pendingDeleteEx = j
            vm.$refs.confirmDeleteEx.show()
        },
        doDeleteExercise() {
            const vm = this
            if (vm.pendingDeleteEx !== null) vm.deleteExercise(vm.pendingDeleteEx)
            vm.pendingDeleteEx = null
        },
        deleteExercise(j) {
            const vm = this
            const list = [...vm.exercises]
            list.splice(j, 1)
            vm.emitExercises(list)
            if (vm.expandedIndex === j) vm.expandedIndex = null
        },
        onExerciseUpdate(j, patch) {
            const vm = this
            const list = [...vm.exercises]
            list.splice(j, 1, { ...list[j], ...patch })
            vm.emitExercises(list)
        },
        setRest(j = null) {
            const vm = this
            if (j >= 0) {
                vm.activeExerciseIndex = j
                let time = vm.exercises[j]?.time || '00:00:00'
                vm.$refs.setRestDialog.showEdit(time)
            } else {
                vm.$refs.setRestDialog.show()
            }
        },
        onRestSave(time) {
            const vm = this
            const list = [...vm.exercises]
            list.splice(vm.activeExerciseIndex, 1, { ...list[vm.activeExerciseIndex], hasTime: true, time })
            vm.emitExercises(list)
        },
    },
}
</script>

<style scoped>
.pfeb-block {
    margin-bottom: 8px;
    border-radius: 12px;
    overflow: hidden;
}

.pfeb-hdr {
    position: relative;
    overflow: hidden;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    cursor: pointer;
    border-radius: 12px;
}

.theme--dark .pfeb-hdr {
    background: linear-gradient(135deg, #1a1f2e 0%, #141824 100%);
    border: 1px solid rgba(204, 255, 51, 0.12);
}

.theme--light .pfeb-hdr {
    background: linear-gradient(135deg, #f0f4f8, #e8edf4);
    border: 1px solid rgba(51, 153, 153, 0.15);
}

.pfeb-hdr.open {
    border-radius: 12px 12px 0 0;
}

.pfeb-hdr::before {
    content: attr(data-num);
    position: absolute;
    right: 84px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 46px;
    font-weight: 900;
    line-height: 1;
    letter-spacing: -2px;
    pointer-events: none;
    z-index: 0;
}

.theme--dark .pfeb-hdr::before {
    color: rgba(204, 255, 51, 0.06);
}

.theme--light .pfeb-hdr::before {
    color: rgba(51, 153, 153, 0.07);
}

.pfeb-drag {
    display: flex;
    cursor: grab;
    color: var(--v-txtMuted-base);
    flex-shrink: 0;
    z-index: 1;
}

.pfeb-drag .v-icon {
    color: inherit !important;
}

.pfeb-block-info {
    flex: 1;
    min-width: 0;
    position: relative;
    z-index: 1;
}

.pfeb-name {
    font-size: 13px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.theme--dark .pfeb-name {
    color: var(--v-primary-base);
}

.theme--light .pfeb-name {
    color: var(--v-accent-base);
}

.pfeb-meta {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 11px;
    color: var(--v-txtMuted-base);
    margin-top: 4px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.pfeb-meta span {
    display: flex;
    align-items: center;
    gap: 4px;
}

.pfeb-meta .v-icon {
    color: var(--v-txtMuted-base) !important;
}

.pfeb-meta-error {
    color: var(--v-error-base);
}

.pfeb-meta-error .v-icon {
    color: var(--v-error-base) !important;
}

.pfeb-ctx-btn {
    flex-shrink: 0;
    z-index: 1;
    color: var(--v-txtSecondary-base) !important;
    background: rgba(255, 255, 255, 0.06) !important;
}

.pfeb-chevron {
    flex-shrink: 0;
    z-index: 1;
    transition: transform 0.22s;
}

.theme--dark .pfeb-chevron {
    color: rgba(204, 255, 51, 0.5) !important;
}

.theme--light .pfeb-chevron {
    color: rgba(51, 153, 153, 0.5) !important;
}

.pfeb-chevron.open {
    transform: rotate(180deg);
}

.pfeb-body {
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-top: none;
    border-radius: 0 0 12px 12px;
    padding: 8px 12px 12px;
}

.rb-exlist {
    min-height: 44px;
}

.rb-empty {
    text-align: center;
    font-size: 12px;
    color: var(--v-error-base);
    padding: 14px 0;
    border: 1px dashed var(--v-outline-base);
    border-radius: 10px;
}

.ghost {
    opacity: 0.5;
}

.es-wrap {
    border: 1px solid var(--v-outline-base);
    border-radius: 10px;
    overflow: hidden;
    margin-bottom: 8px;
    background: var(--v-surface-base);
}

.es-ej-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    cursor: pointer;
    transition: background 0.12s;
}

.es-ej-item:hover {
    background: var(--v-surface2-base);
}

.es-drag {
    display: flex;
    cursor: grab;
    color: var(--v-txtMuted-base);
}

.es-drag .v-icon {
    color: inherit !important;
}

.es-ej-thumb {
    width: 44px;
    height: 38px;
    border-radius: 8px;
    flex-shrink: 0;
    background-size: cover;
    background-position: center;
    position: relative;
}

.es-ej-thumb.has-video::after {
    content: '\25B6';
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    color: rgba(255, 255, 255, 0.9);
    background: rgba(0, 0, 0, 0.28);
    border-radius: 8px;
}

.es-ej-info {
    flex: 1;
    min-width: 0;
}

.es-ej-name {
    font-size: 13px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.es-ej-sets {
    font-size: 11px;
    margin-top: 2px;
    font-weight: 600;
}

.theme--dark .es-ej-sets {
    color: var(--v-primary-base);
}

.theme--light .es-ej-sets {
    color: var(--v-accent-base);
}

.theme--dark .es-ej-sets-error,
.theme--light .es-ej-sets-error {
    color: var(--v-error-base);
}

.es-ej-ctx {
    color: var(--v-txtMuted-base) !important;
}

.es-ej-chev {
    color: var(--v-txtMuted-base) !important;
    transition: transform 0.2s;
    flex-shrink: 0;
}

.es-ej-chev.open {
    transform: rotate(180deg);
}

.es-ej-detail {
    padding: 6px 12px 14px;
    border-top: 1px solid var(--v-outline-base);
}

.pfeb-add-ej {
    text-transform: none !important;
    letter-spacing: normal !important;
    justify-content: flex-start !important;
    padding-left: 10px !important;
}
</style>
